import type { CallQuestion } from './api'
import { VehicleFields, decodeVehicle, isYear, vehicleText } from './vehicles'

/** The conditional question tree, in the browser.
 *
 *  THIS FILE IS THE ONLY COPY OF `call_questions.visible` ON THIS SIDE, and
 *  that is the whole reason it exists rather than the rule being written into
 *  the intake form and again into the working call. A second implementation
 *  that drifts is the most expensive recurring bug in this codebase — the
 *  escalation queue recomputed in the front end handed a coordinator who set
 *  five minutes the hard-coded seven — and the answer there was the same one as
 *  here: one piece of arithmetic, in one place.
 *
 *  WHY THERE IS A COPY AT ALL. The dispatcher's form has to reveal the next
 *  question as he answers, without a round trip per answer, while somebody is
 *  on the phone. Two things keep it honest against the server:
 *
 *  THE RULE IS DELIBERATELY TOO SMALL TO DRIFT. One key, a list of values,
 *  equality, evaluated in list order. No AND, no OR, no NOT, no nesting —
 *  migration 047 refuses anything else at the column level, so what is below is
 *  four lines with nothing in it to get subtly wrong. A condition needing "A is
 *  X and B is Y" is written as two questions, the second hanging off the first.
 *
 *  AND THIS COPY DECIDES ONLY WHAT IS DRAWN, NEVER WHAT IS STORED.
 *  `call_questions.record` re-runs the server's own copy on every save and
 *  refuses to store an answer to a question it does not show, so the worst a
 *  drifted browser can do is draw a question whose answer is then discarded —
 *  visible on the next load. It can never write the contradiction the rule
 *  exists to prevent: a ticket saying Street at the top and answering a garage
 *  question underneath.
 */

/** Which questions show, given the answers so far.
 *
 *  A question shows when it has no condition, or when the question its
 *  condition NAMES has already been shown, has been answered, and that answer
 *  is one of `ask_when_values`.
 *
 *  CASCADING FALLS OUT OF THE ORDER and is not a second rule: a question is
 *  only ever tested against answers to questions ALREADY SHOWN, so if the
 *  garage question disappears because the location changed to Street, anything
 *  hanging off the garage answer disappears with it, however deep.
 *
 *  MATCHING IS EXACT STRING EQUALITY. The values were picked off a list rather
 *  than typed, so there is nothing to be forgiving about — and the server's
 *  reporting index is a plain btree on the stored text, so a case-insensitive
 *  match here would be a second definition of equality that every report
 *  disagreed with.
 */
/** WHAT THE CALL ITSELF SAYS, beside the answers (077): whether the caller
 *  is NOT staying on scene, which asks the questions asked only then. */
export type Asking = { callerLeaving?: boolean }

export function visible(
  tree: readonly CallQuestion[],
  answers: Readonly<Record<string, string>>,
  asking?: Asking,
): CallQuestion[] {
  const shown: CallQuestion[] = []
  const given: Record<string, string[]> = {}
  for (const question of tree) {
    // "Where will the keys be?" -- asked only when the caller is leaving.
    if (question.ask_when_caller_leaves && !asking?.callerLeaving) continue
    const condition = question.ask_when_key
    if (condition !== null) {
      const had = given[condition]
      // Unanswered reads as "not one of the values", exactly as it does on the
      // server: an unshown or unanswered question cannot satisfy anything.
      if (had === undefined) continue
      // SEVERAL ANSWERS (076): ANY of them satisfies it.
      if (!had.some((v) => (question.ask_when_values ?? []).includes(v))) continue
    }
    shown.push(question)
    const raw = answers[question.key] ?? ''
    const values = (isMultiple(question) ? sentValues(raw) : [raw.trim()]).filter(Boolean)
    if (values.length) given[question.key] = values
  }
  return shown
}

/* ---------------------------------------------------------------------------
 * MORE THAN ONE ANSWER (076) -- the browser's copy of `call_questions.picked`
 * and its two helpers. A question ticked `multiple` takes any number of its
 * answers ("which tires are flat"); the value travels as a JSON list, like a
 * vehicle travels as JSON, and nothing picked travels as "" so every other
 * rule reads it as unanswered.
 * ------------------------------------------------------------------------- */

export function isMultiple(question: CallQuestion): boolean {
  return !!question.multiple && question.kind === 'choice'
}

function sentValues(raw: string): string[] {
  const text = raw.trim()
  if (!text) return []
  if (text.startsWith('[')) {
    try {
      const list = JSON.parse(text)
      if (Array.isArray(list)) return list.map((x) => String(x ?? '').trim()).filter(Boolean)
    } catch { /* one plain answer */ }
  }
  return [text]
}

/** The answers given: for several, the ones that are options, IN THE
 *  QUESTION'S ORDER however they were pressed; otherwise the answer itself. */
export function picked(question: CallQuestion, raw: string): string[] {
  if (!isMultiple(question)) {
    const text = raw.trim()
    return text ? [text] : []
  }
  const given = new Set(sentValues(raw))
  return (question.options ?? []).filter((o) => given.has(o))
}

/** Several answers as they travel; "" for none. */
export function wireValues(values: readonly string[]): string {
  return values.length ? JSON.stringify(values) : ''
}

/** The value after pressing one answer of several: on if it was off, off if
 *  it was on, in the question's own order. */
export function toggled(question: CallQuestion, raw: string, option: string): string {
  const now = new Set(picked(question, raw))
  if (now.has(option)) now.delete(option)
  else now.add(option)
  return wireValues((question.options ?? []).filter((o) => now.has(o)))
}

/** How several answers read: "Front left, Rear right". */
export function answerText(question: CallQuestion, raw: string): string {
  return isMultiple(question) ? picked(question, raw).join(', ') : raw.trim()
}

/** The call code these answers make: FFT, BFT, FRFT, or FT with nothing
 *  answered. Null when the nature has no code.
 *
 *  THE BROWSER COPY OF `call_questions.call_code_for` and its `piece_for` and
 *  `compose_code`, under the same guarantee as `visible()` above: it decides
 *  only what is DRAWN, on a call not yet taken. `tools/call_questions_test.py`
 *  pins the customer's examples against the server's copy. A call already open
 *  shows `Incident.call_code` as stored, and never this.
 */
export function callCode(
  natureCode: string | null | undefined,
  tree: readonly CallQuestion[],
  answers: Readonly<Record<string, string>>,
  asking?: Asking,
): string | null {
  // A nature with no code composes to none. The pieces alone would be a code
  // nobody defined, said over the radio as though somebody had.
  if (!natureCode) return null
  return pieces(visible(tree, answers, asking), answers).join('') + natureCode
}

/** Each shown question's piece of the code, in order. */
function pieces(
  shown: readonly CallQuestion[], answers: Readonly<Record<string, string>>,
): string[] {
  return shown.map((question) => {
    if (question.kind !== 'choice') return ''
    // By position: the piece at N belongs to the option at N. An answer that
    // is not one of today's options has no position to take a piece from.
    // SEVERAL (076): each picked answer's piece, in the question's order.
    return picked(question, answers[question.key] ?? '').map((one) => {
      const at = (question.options ?? []).indexOf(one)
      return at < 0 ? '' : question.option_codes?.[at] ?? ''
    }).join('')
  })
}

/** SEVERAL NATURES (076): each nature's questions in turn, a question asked
 *  ONCE -- the first nature that asks it keeps it -- and each tagged `owner`.
 *  The browser's copy of `call_questions.tree_for_natures`, for the Take a
 *  call form before a call exists. */
export function mergeTrees(trees: readonly (readonly CallQuestion[])[]): CallQuestion[] {
  const seen = new Set<string>()
  const merged: CallQuestion[] = []
  trees.forEach((tree, owner) => {
    for (const question of tree) {
      if (seen.has(question.key)) continue
      seen.add(question.key)
      merged.push({ ...question, owner })
    }
  })
  return merged
}

/** The call code of a call with several natures: each nature's part -- its
 *  own questions' pieces, then its code -- joined by "+" (BST+LO). One nature
 *  is `callCode` exactly. The browser's copy of `call_questions.compose_codes`.
 *  What is ASKED is judged over the whole tree, so a question one nature asks
 *  because of another's answer still counts. */
export function callCodes(
  codes: readonly (string | null | undefined)[],
  tree: readonly CallQuestion[],
  answers: Readonly<Record<string, string>>,
  asking?: Asking,
): string | null {
  if (codes.length <= 1) return callCode(codes[0], tree, answers, asking)
  const shown = visible(tree, answers, asking)
  const parts = codes.map((code, owner) => {
    if (!code) return null
    const mine = shown.filter((q) => (q.owner ?? 0) === owner)
    return pieces(mine, answers).join('') + code
  })
  return parts.filter(Boolean).join('+') || null
}

/** The visible questions nobody has answered yet.
 *
 *  THIS IS THE ONLY THING THAT COUNTS THEM ON THIS SIDE, and nothing refuses
 *  anything on the strength of it. Not one question is mandatory: a child
 *  locked in a car goes out in seconds whether or not anybody has said what
 *  kind of road it is, and the rest is filled in after the broadcast.
 */
export function outstanding(
  tree: readonly CallQuestion[],
  answers: Readonly<Record<string, string>>,
  asking?: Asking,
): CallQuestion[] {
  return visible(tree, answers, asking).filter((q) => !picked(q, answers[q.key] ?? '').length)
}

/** THE QUESTIONS THAT BELONG UNDER "Is the caller staying on scene?" (077,
 *  his Q2 A): those asked only when the caller is leaving, and any question
 *  hanging off one of them, however deep. */
export function underStaying(tree: readonly CallQuestion[]): Set<string> {
  const group = new Set<string>()
  for (const question of tree) {
    if (question.ask_when_caller_leaves
      || (question.ask_when_key !== null && group.has(question.ask_when_key))) {
      group.add(question.key)
    }
  }
  return group
}

/** The answers that still apply, after one of them changed.
 *
 *  AN ANSWER TO A QUESTION THAT IS NO LONGER VISIBLE GOES, WITH THE QUESTION.
 *  Change "Garage" to "Street" after saying there was room to work and the
 *  garage answer disappears — left behind it would sit on the ticket
 *  contradicting the location printed above it, and a responder reads a ticket
 *  from the top. `call_questions.record` deletes exactly these rows on the
 *  server, so pruning here is not a second rule: it is this screen agreeing in
 *  advance with what the save is going to do.
 *
 *  IT ALSO DROPS ANYTHING THE TREE DOES NOT ASK. On the working call the form
 *  is seeded from answers already on the record, and a question retired last
 *  week is still on that record with its own frozen prompt — the server refuses
 *  a key its tree does not have, so sending one back would 409 the whole save.
 *  Those answers are shown separately and are never edited.
 */
export function prune(
  tree: readonly CallQuestion[],
  answers: Readonly<Record<string, string>>,
  asking?: Asking,
): Record<string, string> {
  const shown = new Set(visible(tree, answers, asking).map((q) => q.key))
  const kept: Record<string, string> = {}
  for (const [key, value] of Object.entries(answers)) {
    if (shown.has(key) && value.trim()) kept[key] = value
  }
  return kept
}

/** Is this one of the answers the organization marked as the alarm?
 *
 *  ONE DEFINITION, because three screens ask it — the intake form goes red, the
 *  working call goes red, and the ticket's frozen row carries `is_alert` from
 *  the server. Nothing is hardcoded to the word "child": which answers are the
 *  alarm is a column on the question. The server's copy is
 *  `call_questions.alarming`.
 */
export function alerting(question: CallQuestion, answer: string): boolean {
  // SEVERAL (076): red when ANY picked answer is red. One option, as a chip
  // asks about itself, reads as that one.
  const values = isMultiple(question) && !answer.trim().startsWith('[') ? [answer.trim()] : picked(question, answer)
  return values.some((v) => (question.alert_values ?? []).includes(v))
}

/** The line this answer adds to the broadcast, or null when it sends nothing —
 *  the browser's copy of `call_questions.broadcast_for`, for the rehearsal
 *  ticket's badge. What a member actually receives is composed by the server,
 *  and the Settings preview asks it.
 *
 *  A VEHICLE WITH ONLY A PLATE SENDS NOTHING: the plate is for the ticket, so
 *  what would be sent is the rest, and there is no rest. A year that is not a
 *  year is left out here as the server leaves it out of what it stores. */
export function broadcastLine(question: CallQuestion, answer: string): string | null {
  const text = answer.trim()
  if (!text) return null
  if (question.kind === 'choice' && question.option_broadcasts) {
    const lines: string[] = []
    for (const one of picked(question, text)) {
      const at = (question.options ?? []).indexOf(one)
      const line = at < 0 ? '' : question.option_broadcasts[at] || ''
      if (line && !lines.includes(line)) lines.push(line)
    }
    return lines.join(' · ') || null
  }
  if (isMultiple(question)) {
    if (!question.in_broadcast) return null
    const chosen = picked(question, text)
    return chosen.length ? `${question.prompt}: ${chosen.join(', ')}` : null
  }
  if (!question.in_broadcast) return null
  if (question.kind === 'vehicle') {
    const parts = decodeVehicle(answer)
    const sentence = vehicleText(
      { ...parts, year: isYear(parts.year) ? parts.year : '' }, { plate: false })
    return sentence ? `${question.prompt}: ${sentence}` : null
  }
  return `${question.prompt}: ${text}`
}

/** The note for the responder that goes with this answer, or null — by
 *  position, as `call_questions.note_for` reads it. */
export function noteFor(question: CallQuestion, answer: string): string | null {
  if (question.kind !== 'choice') return null
  const notes: string[] = []
  for (const one of picked(question, answer)) {
    const at = (question.options ?? []).indexOf(one)
    const note = at < 0 ? '' : question.option_notes?.[at] || ''
    if (note && !notes.includes(note)) notes.push(note)
  }
  return notes.join(' · ') || null
}

/** The conditional questions, drawn.
 *
 *  ONE COMPONENT FOR THE INTAKE FORM AND THE WORKING CALL. They differ in what
 *  happens to an answer — one is held until the call is created, the other
 *  posts it — and in nothing about how a question looks. A copy per screen is
 *  how the two would come to disagree about what a flagged answer looks like,
 *  which is the one thing on here that has to be unmistakable at three in the
 *  morning.
 *
 *  `alertNote` IS THE CALLER'S because the consequence differs and must be
 *  stated rather than implied: at intake an alarm answer raises the urgency
 *  only if the dispatcher has not set one himself, and on an open call the
 *  raise has already happened or been held. A sentence that guessed which
 *  would be wrong half the time.
 */
export function QuestionFields({
  tree, answers, askedAs, disabled, alertNote, onChange, onCommit, unconfirmed,
  sections, asking, only, keep,
}: {
  tree: readonly CallQuestion[]
  answers: Readonly<Record<string, string>>
  /** THE PROMPT EACH STORED ANSWER WAS ACTUALLY GIVEN UNDER, keyed by question.
   *
   *  The form draws TODAY's wording, which is right -- it is asking today's
   *  question. But the answer sitting in it may have been given under
   *  different words, and rewording a prompt without retiring it is the
   *  ordinary edit on a catalogue the customer has said he will grow
   *  constantly. Unmarked, "Is a child locked inside?" becomes "Is anyone
   *  locked inside?" and every call already carrying a Yes displays that Yes
   *  under the new question, in red, on the screen a dispatcher hands over on
   *  -- while the record underneath says something else.
   *
   *  Freezing the prompt is only half the promise; the other half is SHOWING
   *  when the two disagree. Absent on the intake form, where nothing is stored
   *  yet and every answer is being given under the words on screen. */
  askedAs?: Readonly<Record<string, string>>
  /** A save is in flight, or this role may look and not touch. */
  disabled?: boolean
  alertNote: (question: CallQuestion) => string
  /** The WHOLE answer map, already pruned. Handing back one answer would leave
   *  every caller to work out which others fell away with it. */
  onChange: (next: Record<string, string>) => void
  /** THE ANSWER IS SETTLED — press it on a choice, leave the box on a typed
   *  one. The intake form has no use for it and does not pass one; the working
   *  call saves on it, and the split is what stops a dispatcher typing a
   *  sentence into a text answer from posting it once per keystroke. */
  onCommit?: (next: Record<string, string>) => void
  /** Show a question that EXPECTS an answer in red while it is unanswered.
   *  The working call passes it; the intake form does not — a question the
   *  dispatcher has not reached yet is not a question he skipped. */
  unconfirmed?: boolean
  /** THE NATURES' NAMES, by position (076). With two or more, each nature's
   *  questions are headed by its name. */
  sections?: readonly string[]
  /** What the call says beside the answers (077): the caller not staying. */
  asking?: Asking
  /** WHICH OF THE SHOWN QUESTIONS THIS ONE DRAWS. What is shown is still
   *  decided over the WHOLE tree, so a form split in two places (077: the
   *  keys question under "Is the caller staying on scene?") asks exactly
   *  what one form would. */
  only?: (question: CallQuestion) => boolean
  /** WHAT A PRESS KEEPS when it prunes, if more than `asking` shows (077). A
   *  form that has not stored anything yet passes the caller as leaving, so
   *  "Where will the keys be?" typed and then hidden by Staying is still
   *  there when Not staying is pressed again -- the server stores no answer
   *  to a question it does not ask. */
  keep?: Asking
}) {
  const visibleNow = visible(tree, answers, asking)
  const shown = only ? visibleNow.filter(only) : visibleNow
  const headed = (sections?.length ?? 0) > 1

  function answer(question: CallQuestion, value: string): Record<string, string> {
    const next = prune(tree, { ...answers, [question.key]: value }, keep ?? asking)
    onChange(next)
    return next
  }

  return (
    <>
      {shown.map((question, at) => {
        const given = answers[question.key] ?? ''
        const flagged = alerting(question, given)
        const labelId = `q-${question.id}`
        const many = isMultiple(question)
        const on = new Set(picked(question, given))
        const owner = question.owner ?? 0
        const section = headed && (at === 0 || (shown[at - 1].owner ?? 0) !== owner)
          ? sections?.[owner] : null
        return (
          <div
            className={`qitem ${flagged ? 'flagged' : ''}`}
            key={question.key}
          >
            {section && <div className="qsection">{section}</div>}
            {question.kind === 'choice' ? (
              <>
                {/* NOT A `<label>`, because a label pointing at a group of
                    buttons either points at nothing or clicks the first one —
                    which would ANSWER the question. The group carries the
                    heading instead, which is what a screen reader announces. */}
                <div className="qlabel" id={labelId}>
                  {question.prompt}
                  {many && <span className="qmany"> — press every one that applies</span>}
                </div>
                {askedAs?.[question.key]
                  && askedAs[question.key] !== question.prompt && (
                  <div className="footnote" style={{ margin: '0 0 6px' }}>
                    Answered when this asked: “{askedAs[question.key]}”
                  </div>
                )}
                <div className="chips" role="group" aria-labelledby={labelId}>
                  {(question.options ?? []).map((option) => (
                    <button
                      type="button"
                      key={option}
                      className={
                        `chip ${alerting(question, option) ? 'alarm' : ''}`
                        + `${(many ? on.has(option) : given === option) ? ' on' : ''}`
                      }
                      aria-pressed={many ? on.has(option) : given === option}
                      disabled={disabled}
                      // PRESSING THE CHOSEN ONE AGAIN CLEARS IT. Nothing here
                      // is mandatory, so a mis-click has to be undoable —
                      // otherwise the only way out of a wrong answer is to
                      // choose a different wrong one. An empty value is what
                      // the server reads as "clear this answer".
                      //
                      // TWO STATEMENTS, AND IT HAS TO BE TWO. This was
                      // `onCommit?.(answer(...))`, and an optional CALL
                      // short-circuits its ARGUMENTS: when `onCommit` is
                      // undefined, `answer(...)` is never evaluated at all.
                      // The intake form deliberately passes no `onCommit` --
                      // there is nothing to save until the call exists -- so
                      // every choice chip on it did nothing whatsoever. The
                      // customer found it the only way it could be found:
                      // "I'm trying to use it in the dispatch panel as I am
                      // accepting a call. It's not clickable."
                      //
                      // The typed boxes were fine, because they call `answer`
                      // directly and only the COMMIT is optional -- which is
                      // exactly the shape this now has.
                      onClick={() => {
                        // SEVERAL (076): each press turns one answer on or off.
                        const next = answer(question, many
                          ? toggled(question, given, option)
                          : given === option ? '' : option)
                        onCommit?.(next)
                      }}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </>
            ) : question.kind === 'vehicle' ? (
              <>
                {/* A HEADING AND NOT A `<label>`, for the choice question's
                    reason: there are four boxes, each with a label of its own,
                    and the prompt names the group they make. */}
                <div className="qlabel" id={labelId}>{question.prompt}</div>
                {askedAs?.[question.key]
                  && askedAs[question.key] !== question.prompt && (
                  <div className="footnote" style={{ margin: '0 0 6px' }}>
                    Answered when this asked: “{askedAs[question.key]}”
                  </div>
                )}
                <VehicleFields
                  value={given}
                  labelledBy={labelId}
                  disabled={disabled}
                  onChange={(next) => answer(question, next)}
                  // THE SETTLED VALUE IS HANDED OVER, not read back from
                  // `answers`. A pick changes the value and settles it in the
                  // same moment, before the parent has re-rendered with it, so
                  // `answers` here would still be the form without the pick.
                  // `answer` prunes and hands the parent the same map it gives
                  // back, so what is committed is what is drawn.
                  onSettle={(next) => {
                    const settled = answer(question, next)
                    onCommit?.(settled)
                  }}
                />
              </>
            ) : (
              <>
                <label className="qlabel" htmlFor={labelId}>{question.prompt}</label>
                <input
                  id={labelId}
                  className="qtext"
                  value={given}
                  disabled={disabled}
                  // The server's own ceiling on one answer, mirrored: past it
                  // the refusal is a sentence naming the key, which is not a
                  // thing anybody on this screen has ever seen.
                  maxLength={400}
                  onChange={(e) => answer(question, e.target.value)}
                  // ON BLUR, NOT ON EVERY KEYSTROKE. `answers` is what the
                  // parent already stored from the last `onChange`, so this is
                  // the settled value; committing per keystroke would be one
                  // POST per letter of a sentence typed while somebody is on
                  // the phone.
                  onBlur={() => onCommit?.({ ...answers })}
                />
              </>
            )}
            {/* LOUD, AND IT SAYS WHAT HAPPENED TO THE URGENCY. A red chip on
                its own is a colour, and this is the answer the whole feature
                exists for — a child locked in a car. `role="status"` so it is
                announced when it appears rather than only when somebody
                happens to be reading that part of the form. */}
            {flagged && (
              <p className="qalert" role="status">{alertNote(question)}</p>
            )}
            {/* WHAT THE RESPONDER NEEDS TO KNOW about this answer — "bring a
                spare wheel that fits" — for the dispatcher to pass on. Read off
                the question as it is today, like the red itself; the call
                freezes the note when the answer is stored. */}
            {noteFor(question, given) && (
              <p className="qnote">{noteFor(question, given)}</p>
            )}
            {/* NOT CONFIRMED: asked, expected, and still blank. It refuses
                nothing -- no question may stop a call -- it only makes the gap
                impossible to miss. */}
            {many && on.size > 1 && (
              <p className="qnote">{on.size} picked: {answerText(question, given)}</p>
            )}
            {unconfirmed && question.expect_answer && !picked(question, given).length && (
              <p className="qalert" role="status">Not confirmed yet.</p>
            )}
          </div>
        )
      })}
    </>
  )
}
